<template>
  <transition appear name="fade">
    <div class="login-body row h-100">
      <div class="login-bg-shapes">
        <span class="shape shape-1"></span>
        <span class="shape shape-2"></span>
        <span class="shape shape-3"></span>
      </div>
      <form class="form-signin card align-middle p-4 my-auto" v-on:submit.prevent="">
        <div v-if="logoSrc" class="text-center mb-3 logo-wrap">
          <img :src="logoSrc" class="mb-2 logo-img" />
          <p v-html="logoCopy" class="logo-copy"></p>
        </div>
        <div v-else class="brand-header text-center mb-3">
          <div class="brand-icon"><img :src="defaultLogoSrc" class="mb-2 logo-img" /></div>
          <p class="logo-copy">Welcome to your Points Award Program</p>
        </div>

        <!-- regular login -->
        <transition appear mode="out-in" name="slide-fade">
          <div v-if="login" key="regular-login" class="login-card align-middle">

            <h3 class="login-title">Sign In</h3>

            <p class="space-warning">
              Please Note: Avoid spacing before and after your username, password, and OTP code.
            </p>

            <!-- Login Method Toggle -->
            <div class="method-toggle">
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: loginMethod === 'password' }"
                @click="loginMethod = 'password'"
              >Password</button>
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: loginMethod === 'otp' }"
                @click="loginMethod = 'otp'"
              >OTP</button>
            </div>

            <!-- Username -->
            <div class="field-group">
              <label for="inputUsername" class="field-label">Username</label>
              <div class="input-wrap">
                <span class="input-icon">👤</span>
                <input
                  id="inputUsername"
                  v-model="username"
                  autofocus
                  v-on:focus="focused"
                  class="field-input"
                  placeholder="Enter your username"
                  type="text"
                />
              </div>
              <p class="field-error" v-if="usernameError">{{ usernameError }}</p>
            </div>

            <!-- Password -->
            <div v-if="loginMethod === 'password'" class="field-group">
              <label for="InputPassword" class="field-label">Password</label>
              <div class="input-wrap">
                <span class="input-icon">🔒</span>
                <input
                  id="inputPassword"
                  v-model="password"
                  v-on:focus="focused"
                  class="field-input"
                  placeholder="Enter your password"
                  :type="showPassword ? 'text' : 'password'"
                />
                <button type="button" class="eye-btn" v-on:click="togglePassword" tabindex="-1">
                  <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'"></font-awesome-icon>
                </button>
              </div>
              <p class="field-error" v-if="passwordError">{{ passwordError }}</p>
            </div>

            <!-- OTP Field -->
            <div v-if="loginMethod !== 'password' && otpRequested" class="field-group">
              <label for="otp" class="field-label">OTP Code</label>
              <div class="input-wrap">
                <span class="input-icon">🔑</span>
                <input
                  id="otp"
                  v-model="otp"
                  v-on:focus="focused"
                  class="field-input otp-input"
                  placeholder="Enter OTP"
                  type="text"
                />
              </div>
              <p class="field-error" v-if="passwordError">{{ passwordError }}</p>
            </div>

            <!-- OTP Request Button -->
            <button
              v-if="loginMethod !== 'password'"
              type="button"
              @click="generateOTP(username)"
              class="btn-otp-request"
              :disabled="otpCooldown > 0"
              :class="{ 'cooling': otpCooldown > 0 }"
            >
              <span v-if="otpCooldown > 0">
                <span class="cooldown-ring"></span> Resend in {{ otpCooldown }}s
              </span>
              <span v-else>📨 Request OTP</span>
            </button>

            <!-- OTP Sent Notice -->
            <transition name="fade">
              <div v-if="otpRequested" class="otp-sent-notice">
                <span class="otp-sent-label">OTP sent to:</span>
                <span v-if="!otpEmail" class="dots-loading"></span>
                <strong v-else class="otp-email">{{ otpEmail }}</strong>
              </div>
            </transition>

            <!-- Remember Me + Forgot Password -->
            <div v-if="loginMethod === 'password'" class="row-flex mt-2 mb-3">
              <label class="remember-label">
                <input id="save-info" v-model="rememberMe" type="checkbox" class="remember-check" />
                <span class="remember-text">Remember me</span>
              </label>
              <a class="forgot-link" href="#" v-on:click="login = !login">Forgot password?</a>
            </div>

            <!-- Sign In Buttons -->
            <button
              v-if="loginMethod === 'password'"
              v-on:click="authenticate(username, password)"
              class="btn-primary-action"
              type="button"
            >
              Sign In
            </button>

            <button
              v-if="loginMethod !== 'password' && otpRequested"
              v-on:click="authenticateViaOTP(username, otp)"
              class="btn-primary-action"
              type="button"
            >
              Sign In via OTP
            </button>

          </div>

          <!-- Forgot Password -->
          <div v-else key="lost-pwd" class="forgot-pass-card">
            <button type="button" class="back-btn" v-on:click="login = !login">← Back to login</button>
            <h3 class="login-title mt-2">Reset Password</h3>
            <p class="forgot-desc">Enter your email and we'll send you a new password.</p>
            <div class="field-group">
              <label class="field-label">Email Address</label>
              <div class="input-wrap">
                <span class="input-icon">✉️</span>
                <input
                  id="inputEmail"
                  v-model="email"
                  autofocus
                  class="field-input"
                  placeholder="your@email.com"
                  required
                  type="email"
                />
              </div>
            </div>
            <button v-on:click="requestPasswordEmail(email)" class="btn-primary-action mt-2" type="button">
              Send Reset Email
            </button>
          </div>
        </transition>
      </form>
    </div>
  </transition>
</template>

<script>

import FrontendService from "../../services/FrontendService"
// TODO move these to "store"
const LOCALSTORAGE_USERNAME = "loginUsername";
const LOCALSTORAGE_REMEMBER_ME = "loginRememberMe";
const LOCALSTORAGE_STANDARD_UI_LOGO = "loginStandardUILogo";
const LOCALSTORAGE_STANDARD_UI_COPY = "loginStandardUICopy";
const LOCALSTORAGE_CUSTOM_UI_ID = "loginCustomUIID";

export default {
  name: "login",
  data() {
    return {
      login: true,
      logoSrc: "",
      defaultLogoSrc:"/horizontal_big-transparent-logo.png",
      logoCopy: "Welcome to your Points Award Program",
      username: "",
      password: "",
      email: "",
      rememberMe: false,
      usernameError: "",
      passwordError: "",
      showPassword: false,
      loginMethod: 'password',
      otpRequested: false,
      otp: "",
      otpCooldown: 0,
      cooldownTimer: null,
      otpEmail: "",
    };
  },
  created() {
    console.log("here")
    if (localStorage.getItem(LOCALSTORAGE_USERNAME)) {
      this.username = localStorage.getItem(LOCALSTORAGE_USERNAME);
    }
    if (localStorage.getItem(LOCALSTORAGE_REMEMBER_ME)) {
      try {
        this.rememberMe = JSON.parse(
          localStorage.getItem(LOCALSTORAGE_REMEMBER_ME)
        );
      } catch (e) {
        localStorage.removeItem(LOCALSTORAGE_REMEMBER_ME);
      }
    }
    if (localStorage.getItem(LOCALSTORAGE_STANDARD_UI_LOGO)) {
      this.logoSrc = localStorage.getItem(LOCALSTORAGE_STANDARD_UI_LOGO);
    }
    if (localStorage.getItem(LOCALSTORAGE_CUSTOM_UI_ID)) {
      if (localStorage.getItem(LOCALSTORAGE_CUSTOM_UI_ID) == "divvyAdmin") {
        this.$store.dispatch("user/setIsDivvyAdmin", false);
        localStorage.removeItem("isDivvyAdmin");
        return;
      }
      let customLoginPath =
        "/login/" + localStorage.getItem(LOCALSTORAGE_CUSTOM_UI_ID);
      let resolved = this.$router.resolve(customLoginPath);
      if (resolved.route.name !== "404") {
        this.$router.push(customLoginPath);
      }
    }
  },
  destroyed(){
    if(this.$route.name==='Home'){
      window.location.reload();
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    generateOTP(username) {
      this.otpCooldown = 1;
      let data = {
        username: username
      }
      FrontendService.generateOTP(data)
        .then(
          (resp) => {
            if (resp.status !== 200) {
              this.otpCooldown = 0;
              throw new Error(resp.data.message);
            }
            this.otpEmail = resp.data.user_email || "";
            this.otpRequested = true;
            this.startOtpCooldown();
          },
          (err) => {
            this.showError(err.response.message);
          }
        )
        .catch((e) => {
          this.otpCooldown = 0;
          this.showError(e.message);
        });
    },
    startOtpCooldown() {
      this.otpCooldown = 90;
      this.cooldownTimer = setInterval(() => {
        if (this.otpCooldown > 0) {
          this.otpCooldown--;
        } else {
          clearInterval(this.cooldownTimer);
        }
      }, 1000);
    },
    authenticate: function (username, password) {
      this.$store
        .dispatch("auth/logIn", {
          username,
          password,
        })
        .then((resp) => {
          if (resp.status !== 200) {
            throw new Error(resp.status);
          }
          localStorage.setItem(
            LOCALSTORAGE_REMEMBER_ME,
            JSON.stringify(this.rememberMe)
          );
          if (this.rememberMe) {
            localStorage.setItem(LOCALSTORAGE_USERNAME, username);
          } else {
            localStorage.removeItem(LOCALSTORAGE_USERNAME);
          }
          this.$store.dispatch("user/setIsDivvyAdmin", false);

          this.fetchCompany();
          let clientHasHeadphoneCategory = [500387,500085,500087,252,500247,500030,500268,500199,500200,500053,500340,500355,500333,500289,500408];
          let clientHasHeadphoneAccessoriesCategory = [500248,500416];
          let clientHasHeadphonesCategory = [500427];
          let showRootCategory = [500426];

          if (resp.data.enableCustomizeGridLayout == true) {
            if (clientHasHeadphoneCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/20254" });
            } else if (clientHasHeadphoneAccessoriesCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/20267" });
            } else if (clientHasHeadphonesCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/18794" });
            } else {
              this.$router.push({ path: "/shop/category/1" });
            }
          } else {
            if (showRootCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/1" });
            } else {
              this.$router.push({ name: "Home" });
            }
          }
          
          this.$store.dispatch("loginsAndOrdersReport/insertUserLogs");
        })
        .catch((resp) => {
          if (username.endsWith(" ")) {
            this.authenticate(username.slice(0, -1), password);
          } else {
            this.showErrorBox(resp);
          }
        });
    },
    authenticateViaOTP: function (username, otp) {
      this.$store
        .dispatch("auth/logInViaOTP", {
          username,
          otp
        })
        .then((resp) => {
          if (resp.status !== 200) {
            throw new Error(resp.status);
          }
          localStorage.setItem(
            LOCALSTORAGE_REMEMBER_ME,
            JSON.stringify(this.rememberMe)
          );
          if (this.rememberMe) {
            localStorage.setItem(LOCALSTORAGE_USERNAME, username);
          } else {
            localStorage.removeItem(LOCALSTORAGE_USERNAME);
          }
          this.$store.dispatch("user/setIsDivvyAdmin", false);

          this.fetchCompany();
          let clientHasHeadphoneCategory = [500387, 500085, 500087, 252, 500247, 500030, 500268, 500199, 500200, 500053, 500340, 500355, 500333, 500289, 500408];
          let clientHasHeadphoneAccessoriesCategory = [500248,500411,500416];

          if (resp.data.enableCustomizeGridLayout == true) {
            if (clientHasHeadphoneCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/20254" });
            } else if (clientHasHeadphoneAccessoriesCategory.includes(resp.data.clientId)) {
              this.$router.push({ path: "/shop/category/20267" });
            } else {
              this.$router.push({ path: "/shop/category/1" });
            }
          } else {
            this.$router.push({ name: "Home" });
          }

          this.$store.dispatch("loginsAndOrdersReport/insertUserLogs");
        })
        .catch((e) => {
          console.log(e);
          console.log("error authenticating via otp");
          this.showError(e);
        });
    },
    focused() {
      const usernameWrap = document.getElementById("inputUsername")?.closest(".input-wrap");
      if (usernameWrap) usernameWrap.classList.remove("error-box");
      this.usernameError = "";

      const passwordWrap = document.getElementById("inputPassword")?.closest(".input-wrap");
      if (passwordWrap) passwordWrap.classList.remove("error-box");
      this.passwordError = "";
    },
    showError: function (message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        centered: true,
      });
    },
    showErrorBox: function (message) {
      const usernameWrap = document.getElementById("inputUsername")?.closest(".input-wrap");
      const passwordWrap = document.getElementById("inputPassword")?.closest(".input-wrap");

      if (this.username === "" && this.password === "") {
        if (usernameWrap) usernameWrap.classList.add("error-box");
        this.usernameError = "Username is required!";
        if (passwordWrap) passwordWrap.classList.add("error-box");
        this.passwordError = "Password is required!";
      } else if (this.username === "") {
        if (usernameWrap) usernameWrap.classList.add("error-box");
        this.usernameError = "Username is required!";
      } else if (this.password === "") {
        if (passwordWrap) passwordWrap.classList.add("error-box");
        this.passwordError = "Password is required!";
      } else {
        if (usernameWrap) usernameWrap.classList.add("error-box");
        if (passwordWrap) passwordWrap.classList.add("error-box");
        this.passwordError = message;
      }
    },
    requestPasswordEmail: function (email) {
      if (
        !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(
          email
        )
      ) {
        this.showError("Invalid email address");
        return;
      } else {
        this.$store.dispatch("auth/requestPasswordEmail", email).then(
          () => {
            this.showError("Please check your email inbox");
          },
          (err) => {
            this.showError("Unknown error");
          }
        );
      }
    },
    fetchCompany: function () {
      this.$store.dispatch("company/fetchCompany").then(() => {
        if (this.$store.state.company.logoUrl) {
          localStorage.setItem(
            LOCALSTORAGE_STANDARD_UI_LOGO,
            this.$store.state.company.logoUrl
          );
        } else {
          localStorage.removeItem(LOCALSTORAGE_STANDARD_UI_LOGO);
        }
        if (this.$store.state.company.loginCopyHtml) {
          localStorage.setItem(
            LOCALSTORAGE_STANDARD_UI_COPY,
            this.$store.state.company.loginCopyHtml
          );
        } else {
          localStorage.removeItem(LOCALSTORAGE_STANDARD_UI_COPY);
        }
        if (this.$store.state.company.customLoginId) {
          localStorage.setItem(
            LOCALSTORAGE_CUSTOM_UI_ID,
            this.$store.state.company.customLoginId
          );
        } else {
          localStorage.removeItem(LOCALSTORAGE_CUSTOM_UI_ID);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>

/* =====================
   CSS VARIABLES
===================== */
:root {
  --brand-dark: #111827;
  --brand-dark-2: #1f2937;
  --brand-accent: #f59e0b;
  --brand-accent-hover: #d97706;
  --brand-danger: #ef4444;
  --brand-muted: #6b7280;
  --card-radius: 20px;
  --input-radius: 12px;
  --transition: 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* =====================
   TRANSITIONS
===================== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* =====================
   BACKGROUND
===================== */
.login-body {
  position: relative;
  align-items: center;
  justify-content: center;
  width: 100vw;
  max-width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  overflow-x: hidden;
  overflow-y: auto;
  margin: 0 !important;
  box-sizing: border-box;
}

.login-bg-shapes {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.18;
}

.shape-1 {
  width: 500px;
  height: 500px;
  background: #f59e0b;
  top: -150px;
  right: -100px;
  animation: float 8s ease-in-out infinite;
}

.shape-2 {
  width: 350px;
  height: 350px;
  background: #3b82f6;
  bottom: -100px;
  left: -80px;
  animation: float 11s ease-in-out infinite reverse;
}

.shape-3 {
  width: 250px;
  height: 250px;
  background: #8b5cf6;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: float 14s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-30px) scale(1.05); }
}

/* =====================
   CARD
===================== */
.form-signin {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  margin: auto !important;
  border-radius: var(--card-radius) !important;
  background: rgba(255, 255, 255, 0.97) !important;
  border: none !important;
  box-shadow:
    0 25px 60px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255,255,255,0.08) !important;
  padding: 2.5rem 2.25rem !important;
  backdrop-filter: blur(20px);
}

/* =====================
   BRAND / LOGO
===================== */
.logo-wrap img,
.logo-img {
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
}

.brand-header {
  margin-bottom: 0.5rem;
}

.brand-icon {
  font-size: 2.8rem;
  line-height: 1;
  margin-bottom: 0.4rem;
  //filter: drop-shadow(0 2px 8px rgba(245,158,11,0.35));
}

.logo-copy {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.01em;
  margin-bottom: 0;
}

/* =====================
   HEADING
===================== */
.login-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;

  &::after {
    content: none;
  }
}

/* =====================
   SPACE WARNING
===================== */
.space-warning {
  background: #17a2b8;
  border: 1px solid #bee5eb;
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1.25rem;
  line-height: 1.45;
}

/* =====================
   METHOD TOGGLE
===================== */
.method-toggle {
  display: flex;
  background: #f3f4f6;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 1.4rem;
  gap: 4px;
}

.toggle-btn {
  flex: 1;
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all var(--transition);
  outline: none;

  &.active {
    background: #17a2b8;
    color: #fff;
    /*background: #0ea5a4;
    color: #ffffff;*/
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  }

  &:hover:not(.active) {
    color: #374151;
  }
}

/* =====================
   FIELDS
===================== */
.field-group {
  margin-bottom: 1.1rem;
}

.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.4rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.input-wrap {
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  transition: border-color var(--transition), box-shadow var(--transition);
  overflow: hidden;

  &:focus-within {
    border-color: #17a2b8;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
    background: #fff;
  }
}

.input-icon {
  padding: 0 0.6rem 0 0.9rem;
  font-size: 1rem;
  flex-shrink: 0;
  line-height: 1;
}

.field-input {
  flex: 1;
  border: none !important;
  background: transparent !important;
  padding: 0.7rem 0.6rem 0.7rem 0;
  font-size: 0.92rem;
  color: #111827;
  outline: none !important;
  box-shadow: none !important;

  &::placeholder {
    color: #9ca3af;
  }
}

.otp-input {
  letter-spacing: 0.2em;
  font-weight: 700;
  font-size: 1rem;
}

.eye-btn {
  border: none;
  background: transparent;
  padding: 0 0.85rem;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.9rem;
  transition: color var(--transition);
  flex-shrink: 0;
  outline: none;

  &:hover {
    color: #374151;
  }
}

.field-error {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #ef4444 !important;
  font-weight: 600;
}

/* Error states */
.error-box {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.12) !important;
}

/* =====================
   OTP BUTTON
===================== */
.btn-otp-request {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.65rem 1.25rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition);
  margin-bottom: 0.85rem;
  outline: none;

  /*&:hover:not(:disabled) {
    border-color: #f59e0b;
    background: #fffbeb;
    color: #92400e;
  }*/

  &.cooling,
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.cooldown-ring {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #9ca3af;
  border-top-color: #374151;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* =====================
   OTP SENT NOTICE
===================== */
.otp-sent-notice {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  font-size: 0.82rem;
  color: #065f46;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.otp-sent-label {
  font-weight: 600;
}

.otp-email {
  color: #047857;
  font-weight: 800;
  word-break: break-all;
}

.dots-loading::after {
  content: ".";
  animation: dots 1.5s steps(3, end) infinite;
  font-weight: bold;
}

@keyframes dots {
  0%   { content: "."; }
  33%  { content: ".."; }
  66%  { content: "..."; }
  100% { content: "."; }
}

/* =====================
   REMEMBER ME ROW
===================== */
.row-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  cursor: pointer;
  margin: 0;
}

.remember-check {
  width: 16px;
  height: 16px;
  accent-color: #111827;
  cursor: pointer;
}

.remember-text {
  font-size: 0.83rem;
  color: #374151;
  font-weight: 500;
}

.forgot-link {
  font-size: 0.83rem;
  color: #6b7280;
  font-weight: 600;
  text-decoration: none;
  transition: color var(--transition);

  &:hover {
    color: #111827;
    text-decoration: underline;
  }
}

/* =====================
   PRIMARY BUTTON
===================== */
.btn-primary-action {
  display: block;
  width: 100%;
  padding: 0.8rem 1.5rem;
  border-radius: var(--input-radius);
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all var(--transition);
  outline: none;
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(245,158,11,0.15) 0%, transparent 60%);
    opacity: 0;
    transition: opacity var(--transition);
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.25);

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 10px rgba(0,0,0,0.2);
  }
}

/* =====================
   FORGOT PASSWORD CARD
===================== */
.forgot-pass-card {
  .forgot-desc {
    color: #6b7280;
    font-size: 0.88rem;
    margin-bottom: 1.25rem;
    line-height: 1.5;
  }
}

.back-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.83rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: color var(--transition);
  outline: none;

  &:hover {
    color: #111827;
  }
}

/* =====================
   RESPONSIVE
===================== */
@media (max-width: 480px) {
  .login-body {
    background: #0f172a;
    padding: 1rem;
    display: flex !important;
    justify-content: center;
    overflow-x: hidden;
    width: 100vw;
    max-width: 100vw;
  }

  .form-signin {
    padding: 1.75rem 1.4rem !important;
    border-radius: 16px !important;
    margin: auto !important;
    width: calc(100% - 2rem) !important;
    max-width: 400px !important;
    box-shadow: 0 15px 40px rgba(0,0,0,0.5) !important;
    float: none !important;
  }

  .login-title {
    font-size: 1.4rem;
  }

  .brand-icon {
    font-size: 2.2rem;
  }

  .btn-primary-action {
    padding: 0.85rem 1.25rem;
    font-size: 1rem;
  }

  .shape-1, .shape-2, .shape-3 {
    opacity: 0.1;
  }
}

@media (max-width: 360px) {
  .form-signin {
    padding: 1.4rem 1.1rem !important;
  }

  .toggle-btn {
    font-size: 0.78rem;
    padding: 0.45rem 0.5rem;
  }
}
</style>